import {
  Alert,
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Input,
  Separator,
  Table,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { Music, Trash2 } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  ListToolbar,
  LoadingState,
  Toggle,
  usePagination,
} from '@mangovoice/mango-ui'
import type { TranslateFn } from '@mangovoice/mango-ui'

import type {
  MusicOnHoldApi,
  PlaylistListItem,
} from '../api/musicOnHoldApi.types'
import { CreatePlaylistModal } from '../components/CreatePlaylistModal'
import { MOH_QUERY_KEYS } from '../queries/musicOnHoldQueries'
import {
  useDeletePlaylist,
  useGetPlaylists,
} from '../queries/musicOnHoldQueries'
import { type MusicOnHoldTranslateFn, text } from '../types/i18n.types'
import { useDebouncedCallback } from '../utils/useDebouncedCallback'

const isStream = (p: PlaylistListItem): boolean => p.type === 'stream'

// Playable = streams from a URL, OR (a list playlist that) has audio tracks.
// The seeded system playlists (Default, Christmas, etc.) are streams with 0
// tracks but a stream url, so they're playable despite an empty track list.
// An absent track_count (API omitted it) is treated as playable so we never
// hide everything.
//
// `type` — not `url` — is the stream discriminator: the MOH server fills `url`
// in on list playlists too after processing, so `!!url` would misread a
// track-based list as a stream.
//
// NOTE: same rule lives in extensions-ui `isPlayablePlaylist` and
// system-settings-ui `DefaultsSettingsTab`. Keep the three in sync.
const isPlayable = (p: PlaylistListItem): boolean =>
  isStream(p) || p.track_count == null || p.track_count > 0

// Playback URL for a stream-backed playlist. A bare host with no scheme is
// treated as https, matching how the old stream-type list view built its
// audio source. Returns null for list playlists (their `url` is a processed
// artifact, not a preview stream).
const getStreamSource = (p: PlaylistListItem): string | null => {
  if (!isStream(p) || !p.url) return null
  return p.url.startsWith('http') ? p.url : `https://${p.url}`
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return '—'
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return '—'
  }
}

interface PaginationToolbarProps {
  mb?: number
  pageSize: number | 'All'
  onPageSizeChange: (size: number | 'All') => void
  currentPage: number
  totalCount: number
  onPageChange: (page: number) => void
  isLoading: boolean
  t?: TranslateFn
}

const PaginationToolbar: React.FC<PaginationToolbarProps> = ({
  mb,
  pageSize,
  onPageSizeChange,
  currentPage,
  totalCount,
  onPageChange,
  isLoading,
  t,
}) => (
  <Box mb={mb}>
    <ListToolbar
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      totalCount={totalCount}
      onPageChange={onPageChange}
      isLoading={isLoading}
      showToolbar={totalCount > 0}
      pageSizeOptions={[25, 50, 100]}
      t={t}
      i18nPrefix="musicOnHold.pagination"
    />
  </Box>
)

interface PlaylistTableProps {
  playlists: PlaylistListItem[]
  clickable?: boolean
  onRowClick?: (playlist: PlaylistListItem) => void
  isSettingDefault: boolean
  onSetDefault: (playlistId: number) => void
  /**
   * When provided, renders a delete action per row. Omitted for the system
   * playlist table — bundled defaults can't be deleted by tenants.
   */
  onDelete?: (playlist: PlaylistListItem) => void
  t?: MusicOnHoldTranslateFn
}

const PlaylistTable: React.FC<PlaylistTableProps> = ({
  playlists,
  clickable = true,
  onRowClick,
  isSettingDefault,
  onSetDefault,
  onDelete,
  t,
}) => (
  <Box overflowX="auto">
    <Table.Root variant="outline">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader px={4} py={3}>
            {text(t, 'musicOnHold.list.columnName', 'Name')}
          </Table.ColumnHeader>
          <Table.ColumnHeader px={4} py={3} textAlign="center" w="80px">
            {text(t, 'musicOnHold.list.columnDefault', 'Default')}
          </Table.ColumnHeader>
          <Table.ColumnHeader px={4} py={3}>
            {text(t, 'musicOnHold.list.columnTracks', 'Tracks')}
          </Table.ColumnHeader>
          <Table.ColumnHeader px={4} py={3}>
            {text(t, 'musicOnHold.list.columnPlayer', 'Preview')}
          </Table.ColumnHeader>
          <Table.ColumnHeader px={4} py={3}>
            {text(t, 'musicOnHold.list.columnUpdated', 'Updated')}
          </Table.ColumnHeader>
          {onDelete && (
            <Table.ColumnHeader px={4} py={3} textAlign="right" w="80px">
              {text(t, 'musicOnHold.list.columnActions', 'Actions')}
            </Table.ColumnHeader>
          )}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {playlists.map((playlist) => {
          const streamSource = getStreamSource(playlist)
          return (
            <Table.Row
              key={playlist.id}
              cursor={clickable && onRowClick ? 'pointer' : undefined}
              _hover={clickable && onRowClick ? { bg: 'bg.subtle' } : undefined}
              onClick={
                clickable && onRowClick ? () => onRowClick(playlist) : undefined
              }
            >
              <Table.Cell px={4} py={3}>
                <HStack gap={2}>
                  <Icon asChild color="fg.muted" boxSize={4}>
                    <Music size={16} />
                  </Icon>
                  <Text fontWeight="medium">{playlist.name}</Text>
                  {!!playlist.system && playlist.system > 0 && (
                    <Badge colorPalette="neutral" size="sm" variant="subtle">
                      {text(t, 'musicOnHold.list.systemBadge', 'System')}
                    </Badge>
                  )}
                  {!isPlayable(playlist) && (
                    <Badge colorPalette="warning" size="sm" variant="subtle">
                      {text(
                        t,
                        'musicOnHold.list.notPlayableBadge',
                        'Not playable',
                      )}
                    </Badge>
                  )}
                </HStack>
              </Table.Cell>
              <Table.Cell px={4} py={3} textAlign="center">
                <Tooltip.Root openDelay={200} closeDelay={0}>
                  <Tooltip.Trigger asChild>
                    <Box
                      as="span"
                      display="inline-flex"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      <Toggle
                        checked={playlist.is_default ?? false}
                        disabled={
                          !isPlayable(playlist) ||
                          playlist.is_default ||
                          isSettingDefault
                        }
                        onCheckedChange={() => onSetDefault(playlist.id)}
                      />
                    </Box>
                  </Tooltip.Trigger>
                  <Tooltip.Positioner>
                    <Tooltip.Content>
                      {!isPlayable(playlist)
                        ? text(
                            t,
                            'musicOnHold.list.defaultDisabledTooltip',
                            'Playlist must have at least one audio track or a stream URL to be set as default.',
                          )
                        : playlist.is_default
                          ? text(
                              t,
                              'musicOnHold.list.defaultCurrentTooltip',
                              'This is the current default playlist.',
                            )
                          : text(
                              t,
                              'musicOnHold.list.defaultSetTooltip',
                              'Set as default playlist',
                            )}
                    </Tooltip.Content>
                  </Tooltip.Positioner>
                </Tooltip.Root>
              </Table.Cell>
              <Table.Cell px={4} py={3}>
                <Text fontSize="sm" color="fg.muted">
                  {isStream(playlist)
                    ? text(t, 'musicOnHold.list.streaming', 'Streaming')
                    : `${playlist.track_count ?? '—'} ${text(t, 'musicOnHold.list.tracksLabel', 'tracks')}`}
                </Text>
              </Table.Cell>
              <Table.Cell px={4} py={3}>
                {streamSource ? (
                  <audio
                    controls
                    preload="none"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      height: 32,
                      minWidth: 200,
                      maxWidth: 300,
                      borderRadius: 999,
                    }}
                  >
                    <source src={streamSource} />
                  </audio>
                ) : (
                  <Text fontSize="sm" color="fg.muted" fontStyle="italic">
                    {(playlist.track_count ?? 0) > 0
                      ? text(
                          t,
                          'musicOnHold.list.previewInDetail',
                          'Open to preview tracks',
                        )
                      : text(t, 'musicOnHold.list.noPreview', 'No audio')}
                  </Text>
                )}
              </Table.Cell>
              <Table.Cell px={4} py={3}>
                <Text fontSize="sm" color="fg.muted">
                  {formatDate(playlist.updated_at)}
                </Text>
              </Table.Cell>
              {onDelete && (
                <Table.Cell px={4} py={3} textAlign="right">
                  <Button
                    size="xs"
                    variant="ghost"
                    colorPalette="error"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      onDelete(playlist)
                    }}
                    aria-label={text(
                      t,
                      'musicOnHold.list.deleteAriaLabelNamed',
                      'Delete playlist "{{name}}"',
                    ).replace('{{name}}', playlist.name)}
                  >
                    <Icon asChild>
                      <Trash2 size={14} />
                    </Icon>
                  </Button>
                </Table.Cell>
              )}
            </Table.Row>
          )
        })}
      </Table.Body>
    </Table.Root>
  </Box>
)

export interface MusicOnHoldListPageProps {
  api: MusicOnHoldApi
  pbxId: number
  onRowClick?: (playlist: PlaylistListItem) => void
  onManageAudioTracks?: () => void
  onError?: (error: Error) => void
  t?: MusicOnHoldTranslateFn
  /**
   * Page heading. Defaults to the localized "Music on Hold" (used by web-app);
   * admin3 passes "Playlists" to match its menu + section labels.
   */
  title?: string
}

export const MusicOnHoldListPage: React.FC<MusicOnHoldListPageProps> = ({
  api,
  pbxId,
  onRowClick,
  onManageAudioTracks,
  onError,
  t,
  title,
}) => {
  const queryClient = useQueryClient()
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [isSettingDefault, setIsSettingDefault] = useState(false)

  const [playlistToDelete, setPlaylistToDelete] =
    useState<PlaylistListItem | null>(null)
  const deleteMutation = useDeletePlaylist(api, pbxId)

  const handleDeleteConfirm = async () => {
    if (!playlistToDelete) return
    try {
      await deleteMutation.mutateAsync(playlistToDelete.id)
      setPlaylistToDelete(null)
    } catch (err) {
      onError?.(
        err instanceof Error ? err : new Error('Failed to delete playlist'),
      )
    }
  }

  const handleSetDefault = async (playlistId: number) => {
    if (isSettingDefault) return
    setIsSettingDefault(true)

    const queryKey = MOH_QUERY_KEYS.playlists(pbxId)
    await queryClient.cancelQueries({ queryKey })
    const previous = queryClient.getQueryData<PlaylistListItem[]>(queryKey)

    queryClient.setQueryData<PlaylistListItem[]>(queryKey, (old) =>
      old?.map((p) => ({ ...p, is_default: p.id === playlistId })),
    )

    try {
      await api.setDefaultPlaylist(pbxId, playlistId)
      await queryClient.invalidateQueries({ queryKey })
    } catch (err) {
      queryClient.setQueryData(queryKey, previous)
      onError?.(
        err instanceof Error
          ? err
          : new Error('Failed to set default playlist'),
      )
    } finally {
      setIsSettingDefault(false)
    }
  }

  const handleCreatePlaylist = async (name: string) => {
    setIsSaving(true)
    try {
      await api.createPlaylist({ name, type: 'list', pbx_id: pbxId })
      await queryClient.invalidateQueries({
        queryKey: MOH_QUERY_KEYS.playlists(pbxId),
      })
      setCreateOpen(false)
    } catch (err) {
      onError?.(
        err instanceof Error ? err : new Error('Failed to create playlist'),
      )
    } finally {
      setIsSaving(false)
    }
  }

  const {
    data: rawPlaylists = [],
    isLoading,
    error,
  } = useGetPlaylists(api, pbxId)

  const allPlaylists = rawPlaylists

  const userPlaylists = useMemo(
    () => allPlaylists.filter((p) => !p.system || p.system === 0),
    [allPlaylists],
  )
  const systemPlaylists = useMemo(
    () => allPlaylists.filter((p) => p.system && p.system > 0),
    [allPlaylists],
  )

  const filteredUser = useMemo(() => {
    if (!debouncedSearch) return userPlaylists
    const term = debouncedSearch.toLowerCase()
    return userPlaylists.filter(
      (p) => p.name.toLowerCase().includes(term) || String(p.id).includes(term),
    )
  }, [userPlaylists, debouncedSearch])

  const filteredSystem = useMemo(() => {
    if (!debouncedSearch) return systemPlaylists
    const term = debouncedSearch.toLowerCase()
    return systemPlaylists.filter(
      (p) => p.name.toLowerCase().includes(term) || String(p.id).includes(term),
    )
  }, [systemPlaylists, debouncedSearch])

  const totalCount = filteredUser.length + filteredSystem.length
  const {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    offset,
    totalPages,
  } = usePagination(filteredUser.length)

  // Re-clamp the page when the list shrinks (e.g. deleting the last playlist on
  // the final page). usePagination only resets on pageSize change, so without
  // this the page would slice to empty and strand the toolbar on a stale page.
  useEffect(() => {
    const lastPage = Math.max(1, totalPages)
    if (currentPage > lastPage) setCurrentPage(lastPage)
  }, [currentPage, totalPages, setCurrentPage])

  const paginatedUser = useMemo(() => {
    if (pageSize === 'All') return filteredUser
    return filteredUser.slice(offset, offset + (pageSize as number))
  }, [filteredUser, pageSize, offset])

  const applySearch = useDebouncedCallback((value: string) => {
    setDebouncedSearch(value)
    setCurrentPage(1)
  }, 300)

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value)
      applySearch(e.target.value)
    },
    [applySearch],
  )

  const listToolbarT: TranslateFn | undefined = t
    ? (key: string) => t(key) ?? ''
    : undefined

  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingState
          layout="alert"
          title={text(t, 'musicOnHold.list.loading', 'Loading playlists...')}
        />
      )
    }
    if (error) {
      return (
        <ErrorState
          layout="alert"
          title={text(t, 'musicOnHold.list.error', 'Error')}
          description={(error as Error).message}
        />
      )
    }
    if (totalCount === 0) {
      if (debouncedSearch) {
        return (
          <EmptyState
            layout="alert"
            title={text(t, 'musicOnHold.list.noResults', 'No results')}
            description={text(
              t,
              'musicOnHold.list.noResultsDescription',
              `No playlists matching "${debouncedSearch}".`,
            )}
          />
        )
      }
      return (
        <EmptyState
          layout="alert"
          title={text(t, 'musicOnHold.list.empty', 'No playlists found')}
          description={text(
            t,
            'musicOnHold.list.emptyDescription',
            'There are currently no playlists configured for this PBX.',
          )}
        />
      )
    }

    return (
      <>
        {paginatedUser.length > 0 && (
          <Box marginBottom={6}>
            <Heading fontSize={'md'} mb={3} color="fg.muted">
              {text(t, 'musicOnHold.list.myPlaylists', 'My Playlists')}
            </Heading>
            <PlaylistTable
              playlists={paginatedUser}
              onRowClick={onRowClick}
              isSettingDefault={isSettingDefault}
              onSetDefault={handleSetDefault}
              onDelete={setPlaylistToDelete}
              t={t}
            />
          </Box>
        )}
        <Separator />
        {filteredSystem.length > 0 && (
          <Box marginTop={6}>
            <Heading fontSize={'md'} mb={3} color="fg.muted">
              {text(t, 'musicOnHold.list.systemPlaylists', 'System Playlists')}
            </Heading>
            <PlaylistTable
              playlists={filteredSystem}
              clickable={!!onRowClick}
              onRowClick={onRowClick}
              isSettingDefault={isSettingDefault}
              onSetDefault={handleSetDefault}
              t={t}
            />
          </Box>
        )}
      </>
    )
  }

  return (
    <Box flex="1" w="full" p={6} overflow="auto">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={4}
        mb={6}
      >
        <Heading fontSize="2xl">
          {title ?? text(t, 'musicOnHold.list.title', 'Music on Hold')}
        </Heading>
        <HStack gap={3} flex="1" justify="flex-end" minW="0">
          <Input
            placeholder={text(
              t,
              'musicOnHold.list.searchPlaceholder',
              'Search playlists...',
            )}
            value={searchInput}
            onChange={handleSearchChange}
            size="sm"
            borderRadius="md"
            maxW="xs"
          />
          <Button
            variant="outline"
            size="sm"
            borderRadius="lg"
            onClick={onManageAudioTracks}
            disabled={!onManageAudioTracks}
          >
            {text(t, 'musicOnHold.list.manageTracksButton', 'Audio Tracks')}
          </Button>
          <Button
            colorPalette="brand"
            bg="brand.solid"
            color="brand.contrast"
            borderRadius="lg"
            _hover={{ bg: 'brand.emphasized' }}
            _active={{ bg: 'brand.emphasized' }}
            onClick={() => setCreateOpen(true)}
          >
            {text(t, 'musicOnHold.list.createButton', '+ Create Playlist')}
          </Button>
        </HStack>
      </Box>
      <PaginationToolbar
        mb={4}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        currentPage={currentPage}
        totalCount={filteredUser.length}
        onPageChange={setCurrentPage}
        isLoading={isLoading}
        t={listToolbarT}
      />
      {renderContent()}
      <PaginationToolbar
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        currentPage={currentPage}
        totalCount={filteredUser.length}
        onPageChange={setCurrentPage}
        isLoading={isLoading}
        t={listToolbarT}
      />

      <CreatePlaylistModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreatePlaylist}
        isSaving={isSaving}
        t={t}
      />

      <ConfirmDialog
        open={playlistToDelete !== null}
        onClose={() => {
          if (!deleteMutation.isPending) setPlaylistToDelete(null)
        }}
        title={text(
          t,
          'musicOnHold.list.deleteConfirmTitle',
          'Delete playlist?',
        )}
        description={
          playlistToDelete
            ? text(
                t,
                'musicOnHold.list.deleteConfirmBody',
                'Delete "{{name}}"? This cannot be undone.',
              ).replace('{{name}}', playlistToDelete.name)
            : ''
        }
        confirmLabel={text(t, 'musicOnHold.list.deleteConfirm', 'Delete')}
        cancelLabel={text(t, 'musicOnHold.list.deleteCancel', 'Cancel')}
        onConfirm={handleDeleteConfirm}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      >
        {playlistToDelete?.is_default && (
          <Alert.Root status="warning" mt={3}>
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Description>
                {text(
                  t,
                  'musicOnHold.list.deleteDefaultWarning',
                  'This is the default hold-music playlist. Deleting it will fall back to the system default.',
                )}
              </Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}
      </ConfirmDialog>
    </Box>
  )
}
